import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, ShoppingCart } from "lucide-react";
import { useAccountProducts } from "../hooks/useAccountProducts";
import styles from "./AccountProductHistory.module.css";

interface AccountProductHistoryProps {
  accountUuid: string;
  /** All products mentioned across topics — used for cross-reference highlighting. */
  topicProducts: string[];
  /** Resolved product codes from topics — enables exact item_code matching. */
  resolvedItemCodes: string[];
}

/**
 * Collapsible panel showing products ordered by an account.
 *
 * Products that also appear in the current trip report's topics
 * are highlighted for easy cross-reference.
 */
export function AccountProductHistory({
  accountUuid,
  topicProducts,
  resolvedItemCodes,
}: AccountProductHistoryProps) {
  const { products, loading } = useAccountProducts(accountUuid);
  const [expanded, setExpanded] = useState(false);

  // Build lookup sets for cross-reference matching
  const resolvedCodesLower = useMemo(
    () => new Set(resolvedItemCodes.map((c) => c.toLowerCase())),
    [resolvedItemCodes],
  );
  const topicProductsLower = useMemo(
    () => new Set(topicProducts.map((p) => p.toLowerCase())),
    [topicProducts],
  );

  function isHighlighted(itemCode: string, description: string | null): boolean {
    const code = itemCode.toLowerCase();
    // Primary: exact match on resolved sage item codes
    if (resolvedCodesLower.has(code)) return true;
    // Secondary: free-text product name matches item code or description
    if (topicProductsLower.has(code)) return true;
    if (description) {
      const desc = description.toLowerCase();
      return Array.from(topicProductsLower).some((tp) => desc.includes(tp));
    }
    return false;
  }

  const highlightedCount = products.filter((p) =>
    isHighlighted(p.item_code, p.description),
  ).length;

  return (
    <div className={styles.panel}>
      <button
        type="button"
        className={styles.panelHeader}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <ShoppingCart className="h-4 w-4" style={{ color: "var(--color-text-muted)" }} />
        <span className={styles.panelTitle}>
          Order History
          {highlightedCount > 0 && ` (${highlightedCount} match${highlightedCount > 1 ? "es" : ""})`}
        </span>
        {products.length > 0 && (
          <span className={styles.badge}>{products.length}</span>
        )}
        {expanded ? (
          <ChevronUp className="h-4 w-4" style={{ color: "var(--color-text-muted)" }} />
        ) : (
          <ChevronDown className="h-4 w-4" style={{ color: "var(--color-text-muted)" }} />
        )}
      </button>

      {expanded && (
        <div className={styles.panelBody}>
          {loading && (
            <div className={styles.loading}>Loading order history...</div>
          )}
          {!loading && products.length === 0 && (
            <div className={styles.emptyState}>
              No order history available for this account.
            </div>
          )}
          {!loading &&
            products.map((p) => (
              <div
                key={p.item_code}
                className={`${styles.productRow} ${
                  isHighlighted(p.item_code, p.description)
                    ? styles.highlighted
                    : ""
                }`}
              >
                <span className={styles.productCode}>{p.item_code}</span>
                <span className={styles.productDesc}>
                  {p.description || "—"}
                  {p.category && (
                    <span className={styles.productCategory}> · {p.category}</span>
                  )}
                </span>
                <span className={styles.orderCount}>
                  {p.total_orders} order{p.total_orders !== 1 ? "s" : ""}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
